import { OrderType, PickupTimeType, daysOfWeek } from "@/constants/enum";
import { DateTime, Duration } from "luxon";
import { OrderEstimates } from "@/dto/orderEstimates";
import { ScheduleDateType, ScheduledTime } from "@/dto/scheduledTime";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "./sessionStorageUtils";
import { SessionStorageKey } from "./storageKeys";
import { DayPart } from "@/dto/dayPartMenu";
import { TIME_CONSTANTS } from "@/utils/constants";
import {
  getBusinessHoursOfDay,
  getSchedulableDateOptions,
  isWithinBusinessHours,
} from "./scheduleOrderUtils";
import { CartItem } from "@/models/cart";
import { GiftCard } from "@/dto/giftCard";
import { TableData } from "@/dto/tableData";

export const calculatePickupTime = (
  currentDateTime: DateTime,
  businessHours: any,
  leadTime: number,
  timezone: string,
  orderType: OrderType,
  maxDays: number = 3,
  daypartMenu?: DayPart | null,
  t?: any
) => {
  const nowInMerchantZone = currentDateTime.setZone(timezone);
  const { deliveryTime, pickupTime } = getFromSessionStorage<OrderEstimates>(
    SessionStorageKey.ORDER_ESTIMATES,
    {} as OrderEstimates
  );
  const deliveryDelay =
    orderType === OrderType.DELIVERY
      ? Math.round(
          ((deliveryTime ? new Date(deliveryTime).getTime() : 0) -
            (pickupTime ? new Date(pickupTime).getTime() : 0)) /
            1000 /
            60
        )
      : 0;

  // This happens when order type is changed to delivery but delivery address is not entered
  if (orderType === OrderType.DELIVERY && !deliveryDelay) {
    return "";
  }

  const leadTimeDuration = Duration.fromObject({
    minutes: leadTime + deliveryDelay,
  });
  const targetPickupTime = nowInMerchantZone.plus(leadTimeDuration);
  const today = nowInMerchantZone.startOf("day");
  const tomorrow = today.plus({ days: 1 });
  const sessionTime = getFromSessionStorage<ScheduledTime>(
    SessionStorageKey.SCHEDULED_TIME,
    {} as ScheduledTime
  );

  /**
   * If orderType stored in session storage is not same as orderType then ignore the value stored in session stirage and calculate again
   */
  if (
    sessionTime.orderType === orderType &&
    sessionTime?.date?.dateObject &&
    sessionTime?.time?.timeObject
  ) {
    const storedDateTime = DateTime.fromJSDate(
      new Date(sessionTime.time.timeObject),
      { zone: timezone }
    );
    const isInFuture = storedDateTime > nowInMerchantZone;
    const isToday = storedDateTime.hasSame(today, "day");
    const isTomorrow = storedDateTime.hasSame(tomorrow, "day");

    if (isInFuture) {
      let dateDisplay = sessionTime.date.displayValue;
      if (!isTomorrow && dateDisplay.startsWith(t("tomorrow"))) {
        const match = dateDisplay.match(/.*\((.*)\)/);
        if (match) {
          dateDisplay = match[1];
        }
      }
      if (isToday) {
        return t("todayTime", { formattedTime: sessionTime.time.displayValue });
      } else if (isTomorrow) {
        if (dateDisplay.startsWith(t("tomorrow"))) {
          return `${dateDisplay}, ${sessionTime.time.displayValue}`;
        } else {
          return t("tomorrowDateTime", {
            formattedDate: dateDisplay,
            formattedTime: sessionTime.time.displayValue,
          });
        }
      } else {
        return `${dateDisplay}, ${sessionTime.time.displayValue}`;
      }
    }
  }

  const canShowASAP = isWithinBusinessHours(
    nowInMerchantZone,
    businessHours,
    timezone,
    daypartMenu
  );

  if (canShowASAP) {
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: sessionTime?.orderType || OrderType.PICKUP,
      asap: true,
      date: {
        displayValue: t("today"),
        dateObject: nowInMerchantZone.toJSDate(),
      },
      time: {
        displayValue: "",
        timeObject: nowInMerchantZone.toJSDate(),
      },
    });
    return {
      type: PickupTimeType.ASAP,
      epoch: targetPickupTime.toMillis(),
    };
  }

  for (let i = 0; i < maxDays; i++) {
    const dayIndex = (nowInMerchantZone.weekday + i) % 7;
    const day = daysOfWeek[dayIndex];
    const hoursForDay = getBusinessHoursOfDay(businessHours, day, daypartMenu);

    if (hoursForDay?.length > 0) {
      for (const slot of hoursForDay) {
        const start = DateTime.fromFormat(slot.start.padStart(4, "0"), "HHmm", {
          zone: timezone,
        }).set({
          year: nowInMerchantZone.year,
          month: nowInMerchantZone.month,
          day: nowInMerchantZone.day + i,
        });

        const end = DateTime.fromFormat(slot.end.padStart(4, "0"), "HHmm", {
          zone: timezone,
        }).set({
          year: nowInMerchantZone.year,
          month: nowInMerchantZone.month,
          day: nowInMerchantZone.day + i,
        });

        const adjustedEnd = end < start ? end.plus({ days: 1 }) : end;

        let availableStart = start.plus(leadTimeDuration);

        const minute = availableStart.minute;
        const remainder = minute % 15;
        if (remainder !== 0) {
          availableStart = availableStart.plus({ minutes: 15 - remainder });
        }

        if (
          availableStart >= targetPickupTime &&
          availableStart < adjustedEnd
        ) {
          const scheduleDate = {
            displayValue: availableStart.toFormat("MMM d"),
            dateObject: availableStart.toJSDate(),
          };
          const scheduleTime = {
            displayValue: availableStart.toFormat("h:mm a"),
            timeObject: availableStart.toJSDate(),
          };
          const isToday = availableStart.hasSame(today, "day");
          const isTomorrow = availableStart.hasSame(tomorrow, "day");

          saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
            orderType: sessionTime?.orderType || OrderType.PICKUP,
            date: scheduleDate,
            time: scheduleTime,
          });

          return isToday
            ? t("todayTime", { formattedTime: scheduleTime.displayValue })
            : isTomorrow
            ? t("tomorrowDateTime", {
                formattedDate: scheduleDate.displayValue,
                formattedTime: scheduleTime.displayValue,
              })
            : `${scheduleDate.displayValue}, ${scheduleTime.displayValue}`;
        }
      }
    }
  }

  const schedulableDates: ScheduleDateType[] = getSchedulableDateOptions(
    maxDays - 1,
    businessHours,
    timezone,
    t,
    leadTime,
    daypartMenu
  );

  if (schedulableDates?.length == 0) {
    return null;
  } else {
    const nowSnapshot = DateTime.now();
    const userOffset = nowSnapshot.offset;
    const merchantOffset = nowSnapshot.setZone(timezone).offset;
    const offsetDifference =
      (merchantOffset - userOffset) / TIME_CONSTANTS.MINUTES_PER_HOUR;
    for (let i = 0; i < maxDays; i++) {
      const day = daysOfWeek[(nowInMerchantZone.weekday + i - 1) % 7];
      const hoursForDay = getBusinessHoursOfDay(
        businessHours,
        day,
        daypartMenu
      );

      if (hoursForDay?.length > 0) {
        const firstSlot = hoursForDay[0];

        let start = DateTime.fromFormat(
          firstSlot.start.padStart(4, "0"),
          "HHmm",
          {
            zone: timezone,
          }
        ).set({
          year: nowInMerchantZone.year,
          month: nowInMerchantZone.month,
          day: nowInMerchantZone.day + i,
        });

        start = start.plus(leadTimeDuration).plus({ minutes: 15 });

        const minute = start.minute;
        const remainder = minute % 15;
        if (remainder !== 0) {
          start = start.plus({ minutes: 15 - remainder });
        }

        const shiftedStart = start.minus({
          hours: offsetDifference,
        });

        const scheduleDate = {
          displayValue: start.toFormat("MMM d"),
          dateObject: start.toJSDate(),
        };
        const scheduleTime = {
          displayValue: start.toFormat("h:mm a"),
          timeObject: shiftedStart.toJSDate(),
        };
        const isToday = start.hasSame(today, "day");
        const isTomorrow = start.hasSame(tomorrow, "day");

        saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
          orderType: sessionTime?.orderType || OrderType.PICKUP,
          date: scheduleDate,
          time: scheduleTime,
        });

        return isToday
          ? t("todayTime", { formattedTime: scheduleTime.displayValue })
          : isTomorrow
          ? t("tomorrowDateTime", {
              formattedDate: scheduleDate.displayValue,
              formattedTime: scheduleTime.displayValue,
            })
          : `${scheduleDate.displayValue}, ${scheduleTime.displayValue}`;
      }
    }
  }

  return null;
};

export const getCartStatus = (
  storedCart: CartItem[],
  storedGiftCard: GiftCard | null,
  tableData?: TableData | null
) => {
  const cartValue =
    storedCart.reduce((adder: number, item: any) => {
      if (item?.value?.serviceDetails?.priceType === "PER_UNIT") {
        return adder + 1;
      }
      return adder + (item?.value?.quantity || 0);
    }, 0) + (storedGiftCard ? 1 : 0);

  const scanToOrderCartLength = tableData?.order?.cart?.items?.length
    ? tableData?.order?.cart?.items?.length
    : 0;

  const shouldShow =
    storedCart.length > 0 || !!storedGiftCard || scanToOrderCartLength > 0;

  return {
    cartValue,
    shouldShow,
  };
};
